/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { OptionsObject, useSnackbar } from "notistack";
import apiRequest from "utils/apiRequestWithErrorCode";
import { byLastModified } from "utils/sort";
import { useRuleContext } from "../Form/context/ruleContext";

import {
  SecurityDefinition,
  SecurityPolicy,
  StorageProvider,
} from "@dashboard-v3/api";
import { RuleType } from "../common/types";
import {
  ExtendedPolicy,
  PolicyPredicate,
  SecurityOption,
} from "../Form/Steps/CloudStorage/types";
import { getCloudStorage as getStorage } from "pages/Rules/Form/context/ruleSelectors";

type OptionsState = {
  options: SecurityOption[];
  policies: ExtendedPolicy[];
  definitions: SecurityDefinition[];
};

function initState() {
  return { options: [], policies: [], definitions: [] };
}

export default function useSecurityItems() {
  const { t } = useTranslation("rules");
  const { enqueueSnackbar } = useSnackbar();
  const { state: ruleState } = useRuleContext();
  const storage = getStorage(ruleState.rule);
  const [state, setState] = useState<OptionsState>(initState);

  const {
    data: dependencies,
    isLoading: loadingDependencies,
    error: dependenciesError,
  } = useSWR(
    "/securityDependencies",
    async () => {
      const defsPresetsPath = "/platform/security-definitions";
      const customDefsPath = "/security-definitions?offset=0&limit=1000";
      const policiesPath = "/security-policies?limit=1000";

      return Promise.all([
        apiRequest<SecurityDefinition[]>("GET", defsPresetsPath),
        apiRequest<SecurityDefinition[]>("GET", customDefsPath),
        apiRequest<SecurityPolicy[]>("GET", policiesPath),
      ]);
    },
    { revalidateOnFocus: false }
  );

  const itemsKey = dependencies ? ["securityOptions", dependencies] : null;

  const {
    data: items,
    isLoading: loadingItems,
    error: itemsError,
  } = useSWR(itemsKey, async () => {
    if (!dependencies) return;

    const [presetsDefs, userDefs, userPolicies] = dependencies;
    const userDefinitions = userDefs.sort(byLastModified);
    const definitions = presetsDefs.concat(userDefinitions);
    const policies = await toExtendedPolicies(userPolicies, definitions);

    return { policies, definitions };
  });

  const updateState = (update: Partial<OptionsState>) => {
    setState(prev => ({ ...prev, ...update }));
  };

  useEffect(() => {
    if (dependenciesError || itemsError) {
      const errMsg = t("common:errors.fetchError");
      const options: OptionsObject = { variant: "error" };
      enqueueSnackbar(errMsg, options);
      return;
    }

    if (items) updateState({ ...items });
  }, [items, dependenciesError, itemsError]);

  useEffect(() => {
    if (storage) {
      const { policies, definitions } = state;

      if (definitions && policies) {
        const { type } = ruleState;
        const { provider } = storage;
        const policies = state.policies.filter(policyByRuleType(type));
        const defs = definitions.filter(definitionByRuleType(type, provider));
        const options = toSecurityOptions(defs, policies);
        updateState({ options });
      }
    }
  }, [storage?.provider, state.policies, state.definitions]);

  return {
    securityItems: toSecurityItems(items),
    securityOptions: state.options,
    loading: loadingDependencies || loadingItems,
  };
}

const toSecurityItems = (items: {
  policies: ExtendedPolicy[];
  definitions: SecurityDefinition[];
}) => {
  if (!items) return [];
  const { definitions, policies } = items;
  const definitionItems = definitions.map(i => ({ id: i.id, name: i.name }));
  const policyItems = policies.map(i => ({ id: i.id, name: i.name }));

  return [...definitionItems, ...policyItems];
};

async function getPolicyDefinition(id: string, defs: SecurityDefinition[]) {
  if (!id) return null;
  const preset = defs.find(s => s.id === id);

  if (preset) return preset;

  const requestPath = `/security-definitions/${id}`;
  const definition = await apiRequest<SecurityDefinition>("GET", requestPath);

  return definition;
}

function policyByRuleType(ruleType: RuleType) {
  return (policy: ExtendedPolicy) => {
    if (ruleType === "ZIP_ATTACHMENTS") {
      const { defaultDefinition, predicates } = policy;
      const containsPassword = predicates.some(
        ({ definition }) => definition?.individualPassword
      );

      return !defaultDefinition?.individualPassword || !containsPassword;
    }

    return true;
  };
}

function definitionByRuleType(ruleType: RuleType, provider: StorageProvider) {
  return (definition: SecurityDefinition) => {
    const { systemDefault, allowedStorages } = definition;

    if (ruleType === "ZIP_ATTACHMENTS") {
      return systemDefault
        ? allowedStorages.includes(provider) && !definition.individualPassword
        : !definition.individualPassword;
    }

    return systemDefault ? allowedStorages.includes(provider) : true;
  };
}

function populatePredicates(defs: SecurityDefinition[]) {
  return async (predicate: PolicyPredicate) => {
    const { definitionId } = predicate;
    const definition = await getPolicyDefinition(definitionId, defs);

    return { ...predicate, definition };
  };
}

export async function toExtendedPolicies(
  policies: SecurityPolicy[],
  defs: SecurityDefinition[]
): Promise<ExtendedPolicy[]> {
  const policyPromises = policies.map(async policy => {
    const { defaultDefinition } = policy;
    const promises = policy.predicates.map(populatePredicates(defs));
    const extendedPredicates = await Promise.all(promises);
    const extendedDefault = await getPolicyDefinition(defaultDefinition, defs);

    return {
      ...policy,
      predicates: extendedPredicates,
      defaultDefinition: extendedDefault,
    };
  });

  return await Promise.all(policyPromises);
}

type SecurityItems = {
  presets: SecurityDefinition[];
  definitions: SecurityDefinition[];
};

export enum OptionTypes {
  EMPTY = "EMPTY",
  SEARCH = "SEARCH",
  PRESET = "PRESET",
  DEFINITION = "DEFINITION",
  POLICY = "POLICY",
}

const emptySecurityOption: SecurityOption = {
  key: "empty",
  type: OptionTypes.EMPTY,
  text: "cloudStorage.security.empty",
};

const searchOption: SecurityOption = {
  key: crypto.randomUUID(),
  type: OptionTypes.SEARCH,
  label: "cloudStorage.security.searchField",
};

function toSecurityOptions(
  definitions: SecurityDefinition[],
  policies: ExtendedPolicy[]
): SecurityOption[] {
  if (definitions && policies) {
    const definitionItems: SecurityItems = definitions.reduce(
      (acc, item) => {
        if (item.systemDefault) {
          acc.presets.push(item);
        } else {
          acc.definitions.push(item);
        }
        return acc;
      },
      { presets: [], definitions: [] }
    );

    const policyOptions = policies.map(item => ({
      key: item.id,
      type: OptionTypes.POLICY,
      label: "cloudStorage.security.policy.label",
      text: item.name,
      description: item.description,
      value: `${OptionTypes.POLICY}:${item.id}`,
      provider: item.storageType,
      allowFolderCollaboration:
        item.defaultDefinition?.access !== "COLLABORATORS" ||
        !item.predicates.some(p => p.definition?.access === "COLLABORATORS"),
    }));

    const presetOpts = definitionItems.presets.map(item => ({
      key: item.id,
      type: OptionTypes.PRESET,
      access: item.access,
      label: `securityDefinitions:presets.label`,
      text: item.name,
      value: `${OptionTypes.PRESET}:${item.id}`,
      allowFolderCollaboration: item.access !== "COLLABORATORS",
    }));

    const definitionOpts = definitionItems.definitions.map(item => ({
      key: item.id,
      type: OptionTypes.DEFINITION,
      access: item.access,
      label: "securityDefinitions:definitions.label",
      text: item.name,
      value: `${OptionTypes.DEFINITION}:${item.id}`,
      provider: item.allowedStorages[0],
      allowFolderCollaboration: item.access !== "COLLABORATORS",
    }));

    return [
      emptySecurityOption,
      ...presetOpts,
      searchOption,
      ...policyOptions,
      ...definitionOpts,
    ];
  }
  return [];
}
